import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import { MODAL_WIDTHS } from './constants';
import { resolveVideoUrl, resolveIframeSrc } from './helpers';

// ── Base animated modal shell ─────────────────────────────────────────────────
export function EngineModal({ open, onClose, width = 720, children }) {
  const [mounted, setMounted]   = useState(false);
  const [visible, setVisible]   = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
    } else {
      setVisible(false);
      const t = setTimeout(() => setMounted(false), 300);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!mounted) return null;
  const isFullscreen = width === '100%';

  return (
    <Box
      onClick={onClose}
      sx={{ position: 'absolute', inset: 0, zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center', background: visible ? 'rgba(0,0,0,0.75)' : 'rgba(0,0,0,0)', transition: 'background 0.25s ease', backdropFilter: visible ? 'blur(4px)' : 'none' }}
    >
      <Box
        onClick={e => e.stopPropagation()}
        sx={{ position: 'relative', width: isFullscreen ? '100%' : `min(${width}px, calc(100% - 32px))`, maxHeight: isFullscreen ? '100%' : 'calc(100% - 32px)', background: '#111', borderRadius: isFullscreen ? 0 : '14px', overflow: 'hidden', boxShadow: '0 32px 80px rgba(0,0,0,0.7)', transform: visible ? 'scale(1)' : 'scale(0.88)', opacity: visible ? 1 : 0, transition: 'transform 0.28s cubic-bezier(0.34,1.2,0.64,1), opacity 0.22s ease' }}
      >
        <Box onClick={onClose} sx={{ position: 'absolute', top: 10, right: 10, zIndex: 10, width: 32, height: 32, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', border: '1px solid rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'rgba(255,255,255,0.8)', fontSize: 13, fontWeight: 700, transition: 'all 0.15s', '&:hover': { background: 'rgba(220,38,38,0.8)', borderColor: 'transparent', color: '#fff' } }}>✕</Box>
        {children}
      </Box>
    </Box>
  );
}

// ── Video modal ───────────────────────────────────────────────────────────────
export function VideoModal({ hotspot, onClose }) {
  const action   = hotspot.hotspotAction;
  const provider = action?.videoProvider || 'url';
  const src      = resolveVideoUrl(provider, action?.videoUrl || '');
  const isEmbed  = provider === 'youtube' || provider === 'vimeo';

  return (
    <EngineModal open onClose={onClose} width={MODAL_WIDTHS.lg}>
      <Box sx={{ position: 'relative', paddingBottom: '56.25%', background: '#000' }}>
        {isEmbed ? (
          <iframe src={src} title={hotspot.label || 'Video'} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }} />
        ) : (
          <video src={src} autoPlay={action?.autoplay} controls={action?.controls !== false} playsInline style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />
        )}
      </Box>
    </EngineModal>
  );
}

// ── Iframe modal ──────────────────────────────────────────────────────────────
export function IframeModal({ hotspot, onClose }) {
  const action     = hotspot.hotspotAction;
  const widthKey   = action?.modalWidth || 'md';
  const modalWidth = MODAL_WIDTHS[widthKey] || MODAL_WIDTHS.md;
  const src        = resolveIframeSrc(action?.embedCode || '');
  const isFullscreen = widthKey === 'full';

  return (
    <EngineModal open onClose={onClose} width={modalWidth}>
      {src ? (
        <Box sx={{ width: '100%', height: isFullscreen ? '100vh' : '70vh', minHeight: 300 }}>
          <iframe src={src} title={hotspot.label || 'Content'} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen" allowFullScreen style={{ width: '100%', height: '100%', display: 'block', border: 'none' }} />
        </Box>
      ) : (
        <Box sx={{ width: '100%', minHeight: 200, p: 2, color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }} dangerouslySetInnerHTML={{ __html: action?.embedCode || '' }} />
      )}
    </EngineModal>
  );
}

// ── Gallery modal ─────────────────────────────────────────────────────────────
export function GalleryModal({ hotspot, onClose, isMobile }) {
  const images = hotspot.hotspotAction?.images || [];

  const [idx,        setIdx]        = useState(0);
  const [mounted,    setMounted]    = useState(false);
  const [visible,    setVisible]    = useState(false);
  const [zoomed,     setZoomed]     = useState(false);
  const [zoomScale,  setZoomScale]  = useState(1);
  const [zoomOrigin, setZoomOrigin] = useState({ x: 50, y: 50 });
  const [panOffset,  setPanOffset]  = useState({ x: 0, y: 0 });
  const [dragX,      setDragX]      = useState(0);
  const [dragging,   setDragging]   = useState(false);

  const areaRef  = useRef(null);
  const trackRef = useRef(null);

  const gestureRef = useRef({
    swipeActive:    false,
    startX:         0,
    startY:         0,
    startTime:      0,
    curX:           0,
    pinchActive:    false,
    pinchStartDist: 0,
    pinchStartScale: 1,
    panActive:      false,
    panStartX:      0,
    panStartY:      0,
    panStartOX:     0,
    panStartOY:     0,
    axisDetermined: false,
    axisLocked:     null,
  });

  const zoomedRef    = useRef(false);
  const zoomScaleRef = useRef(1);
  const panOffsetRef = useRef({ x: 0, y: 0 });
  const idxRef       = useRef(0);

  useEffect(() => { zoomedRef.current    = zoomed; },    [zoomed]);
  useEffect(() => { zoomScaleRef.current = zoomScale; }, [zoomScale]);
  useEffect(() => { panOffsetRef.current = panOffset; }, [panOffset]);
  useEffect(() => { idxRef.current       = idx; },       [idx]);

  const [isMobileView, setIsMobileView] = useState(
    isMobile ?? (typeof window !== 'undefined' && window.innerWidth <= 1024)
  );
  useEffect(() => {
    if (isMobile !== undefined) { setIsMobileView(isMobile); return; }
    const check = () => setIsMobileView(window.innerWidth <= 1024);
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, [isMobile]);

  useEffect(() => {
    setMounted(true);
    requestAnimationFrame(() => requestAnimationFrame(() => setVisible(true)));
  }, []);

  const close = () => { setVisible(false); setTimeout(onClose, 280); };

  // Reset zoom & pan on slide change
  useEffect(() => {
    setZoomed(false);
    setZoomScale(1);
    setPanOffset({ x: 0, y: 0 });
    setDragX(0);
  }, [idx]);

  const prev = useCallback(() => setIdx(i => Math.max(0, i - 1)), []);
  const next = useCallback(() => setIdx(i => Math.min(images.length - 1, i + 1)), [images.length]);

  // ── Desktop mouse zoom ────────────────────────────────────────────────────
  const handleMouseClick = useCallback((e, slideIdx) => {
    if (slideIdx !== idxRef.current) return;
    const img = e.currentTarget.querySelector('img');
    if (!img) return;
    const rect = img.getBoundingClientRect();
    const ox = ((e.clientX - rect.left) / rect.width)  * 100;
    const oy = ((e.clientY - rect.top)  / rect.height) * 100;
    if (zoomedRef.current) {
      setZoomed(false); setZoomScale(1); setPanOffset({ x: 0, y: 0 });
    } else {
      setZoomed(true); setZoomScale(2.4); setZoomOrigin({ x: ox, y: oy });
    }
  }, []);

  const handleMouseMove = useCallback((e, slideIdx) => {
    if (slideIdx !== idxRef.current || !zoomedRef.current) return;
    const img = e.currentTarget.querySelector('img');
    if (!img) return;
    const rect = img.getBoundingClientRect();
    setZoomOrigin({
      x: ((e.clientX - rect.left) / rect.width)  * 100,
      y: ((e.clientY - rect.top)  / rect.height) * 100,
    });
  }, []);

  // ── Touch handlers ────────────────────────────────────────────────────────
  const handleTouchStart = useCallback((e) => {
    const g = gestureRef.current;
    if (e.touches.length === 1) {
      const t = e.touches[0];
      g.swipeActive    = true;
      g.startX         = t.clientX;
      g.startY         = t.clientY;
      g.curX           = t.clientX;
      g.startTime      = Date.now();
      g.pinchActive    = false;
      g.axisDetermined = false;
      g.axisLocked     = null;
      if (zoomedRef.current) {
        g.panActive  = true;
        g.panStartX  = t.clientX;
        g.panStartY  = t.clientY;
        g.panStartOX = panOffsetRef.current.x;
        g.panStartOY = panOffsetRef.current.y;
      } else {
        g.panActive = false;
      }
    } else if (e.touches.length === 2) {
      g.swipeActive     = false;
      g.panActive       = false;
      g.pinchActive     = true;
      g.axisDetermined  = false;
      g.axisLocked      = null;
      g.pinchStartDist  = Math.hypot(
        e.touches[1].clientX - e.touches[0].clientX,
        e.touches[1].clientY - e.touches[0].clientY
      );
      g.pinchStartScale = zoomScaleRef.current;
      setDragging(false);
      setDragX(0);
    }
  }, []);

  const handleTouchMove = useCallback((e) => {
    const g = gestureRef.current;

    if (g.pinchActive && e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[1].clientX - e.touches[0].clientX,
        e.touches[1].clientY - e.touches[0].clientY
      );
      const newScale = Math.min(5, Math.max(1, g.pinchStartScale * (dist / g.pinchStartDist)));
      setZoomScale(newScale);
      if (newScale > 1) setZoomed(true);
      if (areaRef.current) {
        const rect = areaRef.current.getBoundingClientRect();
        setZoomOrigin({
          x: ((e.touches[0].clientX + e.touches[1].clientX) / 2 - rect.left) / rect.width  * 100,
          y: ((e.touches[0].clientY + e.touches[1].clientY) / 2 - rect.top)  / rect.height * 100,
        });
      }
      return;
    }

    if (!g.swipeActive || e.touches.length !== 1) return;
    const t  = e.touches[0];
    const dx = t.clientX - g.startX;
    const dy = t.clientY - g.startY;
    g.curX   = t.clientX;

    if (g.panActive && zoomedRef.current) {
      setPanOffset({ x: g.panStartOX + dx, y: g.panStartOY + dy });
      return;
    }

    // Determine axis on first significant movement
    if (!g.axisDetermined) {
      if (Math.abs(dx) > 6 || Math.abs(dy) > 6) {
        g.axisDetermined = true;
        g.axisLocked     = Math.abs(dy) > Math.abs(dx) ? 'vertical' : 'horizontal';
      }
      return;
    }

    if (g.axisLocked === 'vertical') {
      g.swipeActive = false;
      return;
    }

    // Horizontal swipe — update track drag offset
    setDragging(true);
    const atStart = idxRef.current === 0                  && dx > 0;
    const atEnd   = idxRef.current === images.length - 1 && dx < 0;
    setDragX(atStart || atEnd ? dx * 0.25 : dx);
  }, [images.length]);

  const handleTouchEnd = useCallback(() => {
    const g = gestureRef.current;

    if (g.pinchActive) {
      g.pinchActive = false;
      if (zoomScaleRef.current < 1.15) {
        setZoomed(false); setZoomScale(1); setPanOffset({ x: 0, y: 0 });
      }
      return;
    }

    const wasDragging = dragging;
    setDragging(false);
    setDragX(0);
    g.panActive = false;

    if (!g.swipeActive) return;
    g.swipeActive = false;
    if (zoomedRef.current) return;

    const dx       = g.curX - g.startX;
    const dt       = Date.now() - g.startTime;
    const velocity = Math.abs(dx) / dt;

    const W = areaRef.current?.offsetWidth || 300;
    if (wasDragging && (Math.abs(dx) > W * 0.3 || velocity > 0.5)) {
      if (dx < 0) next(); else prev();
    }
  }, [dragging, next, prev]);

  if (!mounted) return null;
  const curr = images[idx] || {};
  const isFS = isMobileView;

  const trackTranslate = dragging
    ? `calc(${idx * -100}% - ${idx * 16}px + ${dragX}px)`
    : `calc(${idx * -100}% - ${idx * 16}px)`;

  return (
    <Box
      onClick={isFS ? undefined : close}
      sx={{
        position: 'absolute', inset: 0, zIndex: 200,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: visible ? 'rgba(0,0,0,0.92)' : 'rgba(0,0,0,0)',
        transition: 'background 0.25s ease',
        backdropFilter: visible ? 'blur(8px)' : 'none',
      }}
    >
      <Box
        onClick={e => e.stopPropagation()}
        sx={isFS ? {
          position: 'absolute', inset: 0,
          background: '#000',
          display: 'flex', flexDirection: 'column',
          transform: visible ? 'translateY(0)' : 'translateY(24px)',
          opacity: visible ? 1 : 0,
          transition: 'transform 0.28s cubic-bezier(0.34,1.2,0.64,1), opacity 0.22s ease',
        } : {
          position: 'relative',
          width: 'min(960px, calc(100% - 32px))',
          maxHeight: 'calc(100% - 32px)',
          background: '#0a0a0a',
          borderRadius: '14px',
          overflow: 'hidden',
          boxShadow: '0 32px 80px rgba(0,0,0,0.7)',
          transform: visible ? 'scale(1)' : 'scale(0.88)',
          opacity: visible ? 1 : 0,
          transition: 'transform 0.28s cubic-bezier(0.34,1.2,0.64,1), opacity 0.22s ease',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* Close */}
        <Box
          onClick={close}
          sx={{
            position: 'absolute', top: isFS ? 14 : 10, right: isFS ? 14 : 10, zIndex: 20,
            width: isFS ? 40 : 32, height: isFS ? 40 : 32,
            borderRadius: '50%',
            background: 'rgba(0,0,0,0.6)',
            border: '1px solid rgba(255,255,255,0.18)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
            color: 'rgba(255,255,255,0.85)',
            fontSize: isFS ? 15 : 13, fontWeight: 700,
            WebkitTapHighlightColor: 'transparent',
            '&:hover': { background: 'rgba(220,38,38,0.8)', color: '#fff', borderColor: 'transparent' },
          }}
        >✕</Box>

        {/* Desktop zoom hint */}
        {!isFS && (
          <Box sx={{ position: 'absolute', top: 10, left: 10, zIndex: 10, px: 1.25, py: 0.4, borderRadius: '20px', background: 'rgba(0,0,0,0.5)', color: 'rgba(255,255,255,0.6)', fontSize: '0.65rem', fontWeight: 600, pointerEvents: 'none' }}>
            {zoomed ? 'Move to pan · Click to zoom out' : 'Click image to zoom'}
          </Box>
        )}

        {/* ── Carousel viewport ── */}
        <Box
          ref={areaRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          sx={{
            flex: 1, minHeight: 0,
            overflow: 'hidden',
            position: 'relative',
            background: '#000',
            touchAction: 'none',
            userSelect: 'none',
          }}
        >
          {/* Sliding track */}
          <Box
            ref={trackRef}
            sx={{
              display: 'flex',
              height: '100%',
              gap: '16px',
              transform: `translateX(${trackTranslate})`,
              transition: dragging ? 'none' : 'transform 0.38s cubic-bezier(0.25,1,0.5,1)',
              willChange: 'transform',
            }}
          >
            {images.map((img, i) => {
              const isActive = i === idx;
              return (
                <Box
                  key={i}
                  onMouseMove={!isFS ? (e) => handleMouseMove(e, i) : undefined}
                  onClick={!isFS ? (e) => handleMouseClick(e, i) : undefined}
                  sx={{
                    flexShrink: 0,
                    width: 'calc(100% + 16px)',
                    height: '100%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    overflow: 'hidden',
                    cursor: !isFS ? (isActive && zoomed ? 'zoom-out' : 'zoom-in') : 'default',
                    position: 'relative',
                  }}
                >
                  <Box
                    component="img"
                    src={img.url}
                    alt={img.caption || `Slide ${i + 1}`}
                    draggable={false}
                    sx={{
                      display: 'block',
                      maxWidth: '100%',
                      maxHeight: isFS ? '100%' : '65vh',
                      objectFit: 'contain',
                      userSelect: 'none',
                      WebkitUserDrag: 'none',
                      transform: isActive && zoomed
                        ? isFS
                          ? `scale(${zoomScale}) translate(${panOffset.x / zoomScale}px, ${panOffset.y / zoomScale}px)`
                          : `scale(${zoomScale})`
                        : 'scale(1)',
                      transformOrigin: `${zoomOrigin.x}% ${zoomOrigin.y}%`,
                      transition: dragging ? 'none' : 'transform 0.32s cubic-bezier(0.25,1,0.5,1)',
                    }}
                  />
                </Box>
              );
            })}
          </Box>

          {/* Desktop arrows */}
          {!isFS && idx > 0 && (
            <Box onClick={e => { e.stopPropagation(); prev(); }} sx={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 5, width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: 18, '&:hover': { background: 'rgba(0,0,0,0.8)' } }}>‹</Box>
          )}
          {!isFS && idx < images.length - 1 && (
            <Box onClick={e => { e.stopPropagation(); next(); }} sx={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', zIndex: 5, width: 36, height: 36, borderRadius: '50%', background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: '#fff', fontSize: 18, '&:hover': { background: 'rgba(0,0,0,0.8)' } }}>›</Box>
          )}
        </Box>

        {/* Bottom bar */}
        <Box
          sx={{
            px: isFS ? 2 : 3,
            pt: isFS ? 1.25 : 1.5,
            pb: isFS ? 2.5 : 1.5,
            background: isFS ? 'rgba(0,0,0,0.75)' : '#111',
            backdropFilter: isFS ? 'blur(12px)' : 'none',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            gap: isFS ? 1.25 : 1,
            flexShrink: 0,
          }}
        >
          {(curr.caption || curr.description) && (
            <Box sx={{ fontSize: isFS ? '0.85rem' : '0.82rem', color: 'rgba(255,255,255,0.75)', textAlign: 'center', lineHeight: 1.4, px: 1 }}>
              {curr.caption || curr.description}
            </Box>
          )}

          {images.length > 1 && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: isFS ? 1.5 : 1, width: '100%', justifyContent: 'center' }}>
              {isFS && (
                <Box onClick={prev} sx={{ width: 36, height: 36, borderRadius: '50%', background: idx === 0 ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: idx === 0 ? 'default' : 'pointer', color: idx === 0 ? 'rgba(255,255,255,0.2)' : '#fff', fontSize: 20, flexShrink: 0, WebkitTapHighlightColor: 'transparent', transition: 'background 0.15s' }}>‹</Box>
              )}
              <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flex: isFS ? 1 : 'unset', justifyContent: 'center', overflow: 'hidden' }}>
                {images.map((_, i) => (
                  <Box key={i} onClick={() => setIdx(i)} sx={{ width: i === idx ? 20 : 7, height: 7, borderRadius: 100, background: i === idx ? '#fff' : 'rgba(255,255,255,0.3)', cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0 }} />
                ))}
              </Box>
              <Box sx={{ fontSize: '0.75rem', fontWeight: 600, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.03em', flexShrink: 0 }}>
                {idx + 1} / {images.length}
              </Box>
              {isFS && (
                <Box onClick={next} sx={{ width: 36, height: 36, borderRadius: '50%', background: idx === images.length - 1 ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.14)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: idx === images.length - 1 ? 'default' : 'pointer', color: idx === images.length - 1 ? 'rgba(255,255,255,0.2)' : '#fff', fontSize: 20, flexShrink: 0, WebkitTapHighlightColor: 'transparent', transition: 'background 0.15s' }}>›</Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}